/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends From
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import moment from 'moment';
// Internal Imports
import AdministeredBy from 'linced-app/enums/medical/administered-by';
import AutoinjectorBrand from 'linced-app/enums/medical/autoinjector-brand';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import MedicationType from 'linced-app/enums/medical/medication-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Unit from 'linced-app/enums/medical/unit';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { uniq } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

const autoinjectorTypes = [
  MedicationType.AUTOINJECTOR___STANDARD,
  MedicationType.AUTOINJECTOR___JUNIOR,
];

export default class MedicationEntry extends Component {
  Jurisdiction = Jurisdiction;

  // Services
  @service
  options;

  @service
  session;

  @service
  store;

  @service
  toaster;

  AdministeredBy = AdministeredBy;
  AutoinjectorBrand = AutoinjectorBrand;
  MedicationType = MedicationType;
  Unit = Unit;
  @tracked
  batch = null;
  @tracked
  dialogOpen = false;
  @tracked
  learners = null;
  @tracked
  medicalCondition = null;
  @tracked
  medication = null;
  editorOptions = Object.freeze(defaultTinyMCEConfig());

  @tracked
  protectShortDescription;

  @tracked
  saveInProgress;

  @computed('medication.authorisedStaff.content.[]')
  get remainingStaff() {
    // authorisedStaff is an async hasMany, but the ids are all that's needed.
    let authorisedIds = new Set(
      this.medication.hasMany('authorisedStaffIds').ids(),
    );

    return this.store
      .peekAll('person')
      .filter(
        (person) =>
          person.type === PersonType.STAFF && !authorisedIds.has(person.id),
      );
  }

  @action
  openDialog() {
    if (!this.edit) {
      this.medication = this.store.createRecord('medical/medication', {
        schoolId: this.get('session.school'),
        allowSiblingUse: false,
      });
      if (this.person) {
        this.medication.set('person', this.person);
      }
    } else {
      if (this.medication.medicalCondition) {
        this.medicalCondition = this.medication.medicalCondition;
      }
    }

    this.learners = this.store
      .peekAll('person')
      .filter((person) => person.type === PersonType.LEARNER);

    this.batch = this.store.createRecord('medical/batch', {
      schoolId: this.get('session.school'),
    });

    this.dialogOpen = true;
  }

  @action
  closeDialog() {
    if (!this.medication.get('id')) {
      this.medication.deleteRecord();
      this.medication = null;
    } else {
      // Restore any altered attributes and relationships.
      rollbackRecord(this.medication);
    }
    if (!this.batch.get('id')) {
      this.batch.deleteRecord();
      this.batch = null;
    } else {
      // Restore any altered attributes and relationships.
      rollbackRecord(this.batch);
    }
    if (this.mode !== 'medicalCondition') {
      this.medicalCondition = null;
    }
    this.dialogOpen = false;
  }

  @action
  save() {
    this.saveInProgress = true;
    return this.medication
      .save()
      .then((medication) => {
        this.toaster.success('Medication saved successfully!');
        if (this.batch.hasBeenTouched) {
          let batch = this.batch;
          batch.set('medication', medication);
          batch
            .save()
            .then(() => {
              this.toaster.success('Batch saved successfully!');
            })
            .catch(() => {
              // The dialog has closed, so drop the unsaved batch from the
              // medication's batches.
              rollbackRecord(batch);
              this.toaster.error('Batch save failed!');
            });
        }
        if (this.medicalCondition) {
          let medicationIds = uniq(
            this.medicalCondition.get('medicationIds').toArray(),
          );
          if (!medicationIds.includes(medication.id)) {
            medicationIds.push(medication.id);
            this.medicalCondition.set('medicationIds', medicationIds);
            this.store
              .peekRecord('person', this.medication.person.get('id'))
              .save()
              .catch(() => {
                this.toaster.error('Person save failed!');
              });
          }
        }
      })
      .then(() => {
        this.dialogOpen = false;
        if (this.mode !== 'medicalCondition') {
          this.medicalCondition = null;
        }
      })
      .catch(() => {
        // Stay open to retry; closeDialog discards the unsaved records.
        this.toaster.error('Medication save failed!');
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  @action
  removeLearner() {
    this.medication.setProperties({
      person: null,
      allowSiblingUse: false,
    });
  }

  @action
  setMedicationType(medicationType) {
    this.medication.set('type', medicationType.value);
    if (autoinjectorTypes.includes(this.medication.type)) {
      this.medication.set(
        'shortDescription',
        MedicationType.getLabel(this.medication.type),
      );
      this.protectShortDescription = true;
    } else {
      this.medication.setProperties({
        shortDescription: '',
        autoinjectorBrand: '',
      });
      this.protectShortDescription = false;
    }
  }

  @action
  setAutoinjectorBrand(autoinjectorBrand) {
    this.medication.set('autoinjectorBrand', autoinjectorBrand.value);
  }

  @action
  setAdministeredBy(administeredBy) {
    // Plain array, not A(): authorisedStaff is a @hasMany relationship, and
    // Ember Data's hasMany setter never stores the passed-in array itself -
    // it asserts Array.isArray(value) (true either way) and spreads its
    // items into a splice() call on the real ManyArray, so the wrapper
    // type of the input is irrelevant (verified against the installed
    // ember-data/model source).
    this.medication.setProperties({
      administeredBy: administeredBy.value,
      authorisedStaff: [],
    });
  }

  @action
  setBatchDate(type, date) {
    if (isEmpty(date)) {
      this.batch.set(type, undefined);
      return;
    }
    this.batch.set(type, moment(date, 'YYYY-MM-DD').unix());
    if (type === 'expiryDate' && isEmpty(this.batch.get('reviewDate'))) {
      // Set a default for review date if it is empty
      let today = moment();
      let expiryDate = moment(date, 'YYYY-MM-DD');
      // Set the review date to 1 week before the expiry date
      let reviewDate = moment(date, 'YYYY-MM-DD').subtract(1, 'w');
      // Unless expiry date is less than one week away - then set review date to the expiry date
      if (expiryDate.diff(today, 'd') < 7) {
        reviewDate = expiryDate;
      }
      this.batch.set('reviewDate', reviewDate.unix());
    }
  }

  @action
  setUnit(unit) {
    this.medication.set('unit', unit.value);
  }

  @action
  async addStaff(staff) {
    // resolve the async hasMany relationship upfront.
    (await this.medication.authorisedStaff).push(staff);
  }

  @action
  async removeStaff(staff) {
    // resolve the async hasMany relationship upfront.
    let authorisedStaff = await this.medication.authorisedStaff;
    let index = authorisedStaff.indexOf(staff);
    if (index !== -1) {
      authorisedStaff.splice(index, 1);
    }
  }

  @action
  setMedicalCondition(medicalCondition) {
    this.medicalCondition = medicalCondition;
  }
}
